import React, { useState } from 'react';
import { Download, History, Wallet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrencySymbol } from '../../constants/index.js';
import WithdrawalButton from './WithdrawalButton';
import AvailableBalance from './AvailableBalance';
import WithdrawalHistory from './WithdrawalHistory';

const WithdrawalSection = ({ project }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('balance');
  const [availableBalance, setAvailableBalance] = useState(null);

  // Only show withdrawal section to project owner
  if (!user || project.owner_id !== user.id) {
    return null;
  }

  // Only show withdrawal section for active or completed projects with funding
  if (!['active', 'completed'].includes(project.status) || project.current_funding <= 0) {
    return null;
  }

  // If project is active (not yet completed), show info about when withdrawals will be available
  if (project.status === 'active') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Download className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Withdrawals</h3>
              <p className="text-sm text-gray-600">Available after campaign completion</p>
            </div>
          </div>
        </div>

        {/* Campaign Status Info */}
        <div className="p-6">
          <div className="text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-4">
              Campaign Active
            </div>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Withdrawals Available After Campaign Ends
            </h4>
            <p className="text-gray-600 mb-4">
              Your funds are secure and will be available for withdrawal once your campaign is completed.
            </p>

            {project.days_remaining > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-blue-900 mb-1">
                  Campaign ends in {project.days_remaining} day{project.days_remaining !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-blue-700">
                  Withdrawals will be automatically enabled when your campaign completes
                </p>
              </div>
            )}

            {project.days_remaining === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-green-900 mb-1">
                  Campaign ending soon
                </p>
                <p className="text-xs text-green-700">
                  Your campaign will complete automatically within the next few hours
                </p>
              </div>
            )}

            {/* Project Funding Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Total Raised</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {getCurrencySymbol(project.currency)}{project.current_funding?.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Funding Goal</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {project.funding_goal ? `${getCurrencySymbol(project.currency)}${project.funding_goal.toLocaleString()}` : 'No limit'}
                  </span>
                </div>
              </div>
            </div>

            {/* Info about withdrawal policy */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
              <h5 className="font-medium text-yellow-900 mb-2">Why do we restrict withdrawals?</h5>
              <div className="space-y-1 text-sm text-yellow-800">
                <div className="flex items-start gap-2">
                  <span className="font-medium text-yellow-600">•</span>
                  <span>Ensures campaign commitment and builds donor trust</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium text-yellow-600">•</span>
                  <span>Prevents premature access to crowdfunded resources</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium text-yellow-600">•</span>
                  <span>Follows crowdfunding industry best practices</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleWithdrawalSuccess = (withdrawal) => {
    // Refresh the balance and switch to history tab to show the new withdrawal
    setActiveTab('history');
  };

  const handleBalanceRefresh = (newBalance) => {
    setAvailableBalance(newBalance);
  };

  const tabs = [
    {
      id: 'balance',
      label: 'Balance',
      icon: Wallet,
      count: null
    },
    {
      id: 'history',
      label: 'History',
      icon: History,
      count: null
    }
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Download className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Withdrawals</h3>
              <p className="text-sm text-gray-600">Manage your project funds</p>
            </div>
          </div>

          {/* Withdrawal Button */}
          <WithdrawalButton
            project={project}
            onWithdrawalSuccess={handleWithdrawalSuccess}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 text-sm font-medium border-b-2 transition-colors
                           ${isActive
                    ? 'border-teal-500 text-teal-600 bg-teal-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                                   ${isActive
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-gray-100 text-gray-600'
                    }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4 md:p-6">
        {activeTab === 'balance' && (
          <div className="space-y-6">
            {/* Available Balance */}
            <AvailableBalance
              projectId={project.id}
              project={project}
              onRefresh={handleBalanceRefresh}
            />

            {/* Balance Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">How Withdrawals Work</h4>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <span className="font-medium text-blue-600">•</span>
                  <span>Withdrawals are only available after campaign completion</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium text-blue-600">•</span>
                  <span>Withdrawals ≤10,000 π are automatically processed immediately on Pi blockchain</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium text-blue-600">•</span>
                  <span>Withdrawals {'>'}10,000 π require admin approval, then processed immediately</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium text-blue-600">•</span>
                  <span>Up to 5 withdrawal requests per project per 24 hours</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-medium text-blue-600">•</span>
                  <span>All withdrawals are processed on the Pi blockchain</span>
                </div>
              </div>
            </div>

            {/* Project Funding Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Total Raised</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {getCurrencySymbol(project.currency)}{project.current_funding.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Funding Goal</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {project.funding_goal ? `${getCurrencySymbol(project.currency)}${project.funding_goal.toLocaleString()}` : 'No limit'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <WithdrawalHistory
            projectId={project.id}
            showProjectInfo={false}
          />
        )}
      </div>
    </div>
  );
};

export default WithdrawalSection;
